import { useCallback, useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Bold, Italic, List, ListOrdered, Underline } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip'
import { cn } from '@/lib/utils'
import { sanitizeHtml } from '../utils/sanitizeHtml'

/**
 * Shared by the editor and anything rendering its output, so a note reads the
 * same while it is being written and after it is saved. No `prose` plugin is
 * installed, so the list markers are spelled out.
 */
export const RICH_TEXT_CLASSES =
  'text-sm leading-relaxed break-words [&_a]:underline [&_a]:text-(--ad-primary) [&_ol]:list-decimal [&_ol]:pl-5 [&_ul]:list-disc [&_ul]:pl-5'

/** The formatting the toolbar offers, in the order it is shown. */
const COMMANDS = [
  { command: 'bold', icon: Bold, labelKey: 'notes.bold' },
  { command: 'italic', icon: Italic, labelKey: 'notes.italic' },
  { command: 'underline', icon: Underline, labelKey: 'notes.underline' },
  { command: 'insertUnorderedList', icon: List, labelKey: 'notes.bulletList' },
  { command: 'insertOrderedList', icon: ListOrdered, labelKey: 'notes.numberedList' },
] as const

interface RichTextEditorProps {
  /** Starting HTML. Changing it re-seeds the editor, discarding what was typed. */
  value: string
  onChange: (html: string) => void
  placeholder?: string
  /** Height in px the body starts at; it grows with the text. */
  minHeight?: number
  autoFocus?: boolean
  className?: string
}

/**
 * A small WYSIWYG: bold, italic, underline and lists, nothing else. The body is
 * uncontrolled — writing `value` back into it on every keystroke would move the
 * caret to the end — so `value` seeds it and `onChange` reports what it holds.
 */
export function RichTextEditor({
  value,
  onChange,
  placeholder,
  minHeight = 96,
  autoFocus = false,
  className,
}: RichTextEditorProps) {
  const { t } = useTranslation()
  const bodyRef = useRef<HTMLDivElement>(null)
  /**
   * The last HTML this editor reported. `value` coming back as exactly that is
   * the caller echoing our own keystrokes, not a new document to load — reseeding
   * on it would move the caret to the end of the text on every character typed.
   */
  const emittedRef = useRef<string | null>(null)
  const [active, setActive] = useState<string[]>([])
  const [isEmpty, setIsEmpty] = useState(true)

  // Seeded once per `value` identity: the editor owns its content from then on.
  useEffect(() => {
    const body = bodyRef.current
    if (!body) return
    if (value === emittedRef.current) return
    const html = sanitizeHtml(value)
    if (body.innerHTML !== html) body.innerHTML = html
    setIsEmpty(!body.textContent?.trim())
    if (autoFocus) {
      body.focus()
      // Put the caret after the existing text rather than in front of it.
      const range = document.createRange()
      range.selectNodeContents(body)
      range.collapse(false)
      const selection = window.getSelection()
      selection?.removeAllRanges()
      selection?.addRange(range)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value])

  /** Which marks apply where the caret is, so the toolbar can show them. */
  const syncActive = useCallback(() => {
    setActive(
      COMMANDS.filter(({ command }) => {
        try {
          return document.queryCommandState(command)
        } catch {
          return false
        }
      }).map(({ command }) => command)
    )
  }, [])

  const emit = useCallback(() => {
    const body = bodyRef.current
    if (body) {
      emittedRef.current = body.innerHTML
      onChange(body.innerHTML)
      setIsEmpty(!body.textContent?.trim())
    }
    syncActive()
  }, [onChange, syncActive])

  const run = (command: string) => {
    bodyRef.current?.focus()
    // execCommand is deprecated but is still the only cross-browser way to
    // apply formatting to a selection without shipping an editor framework.
    document.execCommand(command)
    emit()
  }

  /**
   * Pasted markup is reduced to text: whatever the clipboard carries, a note
   * only ever holds the handful of tags this toolbar can produce.
   */
  const onPaste = (event: React.ClipboardEvent) => {
    event.preventDefault()
    document.execCommand('insertText', false, event.clipboardData.getData('text/plain'))
    emit()
  }

  return (
    <div
      className={cn(
        'overflow-hidden rounded-md border bg-background focus-within:border-ring focus-within:ring-[3px] focus-within:ring-ring/50',
        className
      )}
    >
      <div className="flex items-center gap-0.5 border-b px-1 py-1">
        {COMMANDS.map(({ command, icon: Icon, labelKey }) => (
          <Tooltip key={command}>
            <TooltipTrigger asChild>
              <Button
                type="button"
                variant="ghost"
                size="icon-sm"
                aria-label={t(labelKey)}
                aria-pressed={active.includes(command)}
                className={cn(active.includes(command) && 'bg-accent text-accent-foreground')}
                // Keeps the selection in the body — a focus change would drop it.
                onMouseDown={(event) => event.preventDefault()}
                onClick={() => run(command)}
              >
                <Icon />
              </Button>
            </TooltipTrigger>
            <TooltipContent>{t(labelKey)}</TooltipContent>
          </Tooltip>
        ))}
      </div>
      <div
        ref={bodyRef}
        contentEditable
        suppressContentEditableWarning
        role="textbox"
        aria-multiline="true"
        aria-label={placeholder}
        data-placeholder={placeholder}
        style={{ minHeight }}
        className={cn(
          RICH_TEXT_CLASSES,
          'px-3 py-2 outline-none',
          // `:empty` misses the stray <br> a browser leaves behind, so the
          // placeholder is driven by the emptiness flag set on input.
          'data-[empty=true]:before:pointer-events-none data-[empty=true]:before:text-(--ad-text-muted) data-[empty=true]:before:content-[attr(data-placeholder)]'
        )}
        data-empty={isEmpty}
        onInput={emit}
        onBlur={emit}
        onKeyUp={syncActive}
        onMouseUp={syncActive}
        onPaste={onPaste}
      />
    </div>
  )
}
